import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, catchError, filter, firstValueFrom, map, of, Subject, takeUntil } from 'rxjs';

import { HttpService } from './http.service';
import { ErrorService } from './error.service';

import { environment } from '@envs/environment';

import { CHANGESIGNINEMAIL, EDIT_PROFILE, policies, RESET_PASSWORD, SIGN_UP } from '@app/msal/auth-config';
import { User } from '@models/user';

export interface ISsoReply {
  email: string;
  externalAuthId: string;
}

@Injectable({
  providedIn: 'root'
})
export class SsoService implements OnDestroy {

  private isReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private httpService: HttpService,
    private broadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private errorService: ErrorService,
  ) {
    this.broadcastService.msalSubject$
      .pipe(map((msg: EventMessage) => {
        return msg;
      }))
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.LOGOUT_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        this.isReady$.next(true);
      });

    this.broadcastService.inProgress$
      .pipe(
        map((status: InteractionStatus) => {
          this.isReady$.next(false);
          return status;
        }),
      )
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
      )
      .subscribe(() => {
        this.isReady$.next(true);
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  public subscribeRedirect$() {
    return this.msalService.handleRedirectObservable().pipe(catchError((error) => {
      return of(null);
    }));
  }

  public subscribeStatus$() {
    return this.isReady$;
  }

  // make request to the backend to check if the email provided in sso form is already in use
  public checkEmail(email: string): Promise<ISsoReply> {
    return new Promise((resolve, reject) => {
      this.httpService.get('**/sso/check-email', { email: email }).then((response: ISsoReply | any) => {
        return resolve(response);
      }, (err) => {
        return reject(err);
      });
    });
  }

  // link account is designed to link existing sso accounts to existing legacy users
  public linkAccount(externalAuthId: string | null, email?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpService.post('**/sso/link-account', {
        externalAuthId,
        email,
      }).then(() => {
        return resolve();
      }, (err) => {
        return reject(err);
      });
    });
  }

  // link account is designed to link existing sso accounts to existing legacy users
  public unlinkAccount(username: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.httpService.post('**/sso/unlink-account', {
        username,
      }).then(() => {
        return resolve();
      }, (err) => {
        return reject(err);
      });
    });
  }

  public getCurrentAccount(): Promise<AccountInfo | null> {
    return new Promise((resolve, reject) => {
      return firstValueFrom(this.isReady$.pipe(
        filter((isReady) => isReady),
      )).then(() => {
        const accounts: AccountInfo[] = this.msalService.instance.getAllAccounts();
        // traverse all accounts and pick the one with the latest date
        let max: number = 0;
        let maxIndex = -1;
        for (let i = 0; i < accounts.length; i++) {
          const ts = accounts[i].idTokenClaims?.auth_time;
          if (ts && ts > max) {
            max = ts;
            maxIndex = i;
          }
        }

        if (maxIndex >= 0 && accounts[maxIndex]) {
          this.msalService.instance.setActiveAccount(accounts[maxIndex]);
          return resolve(accounts[maxIndex]);
        }

        return resolve(null);
      }).catch((error) => {
        this.errorService.printError(error);
        return reject(error);
      });
    });
  }

  public redirectToRegister(user: User, state: string) {
    this.msalService.loginRedirect({
      authority: policies[SIGN_UP].authority,
      scopes: ['openid', 'profile', 'email'],
      redirectUri: environment.sso.redirectUri,
      redirectStartPage: environment.sso.redirectUri, // process the response on /sso instead of navigating back to the initiating page
      state: state, // the component where call came from
      extraQueryParameters: {
        "givenName": user?.firstname || "",
        "surName": user?.lastname || "",
      },
      loginHint: user?.email || "",
    });
  }

  public redirectToProfileEdit() {
    this.msalService.loginRedirect({
      authority: policies[EDIT_PROFILE].authority,
      scopes: ['openid', 'profile', 'email'],
      redirectUri: environment.sso.redirectUri,
      redirectStartPage: environment.sso.redirectUri,
      state: 'settings', // the component where call came from
    });
  }

  public redirectToChangeEmail() {
    this.msalService.loginRedirect({
      authority: policies[CHANGESIGNINEMAIL].authority,
      scopes: ['openid', 'profile', 'email'],
      redirectUri: environment.sso.redirectUri,
      redirectStartPage: environment.sso.redirectUri,
      state: 'settings', // the component where call came from
    });
  }

  public redirectToPasswordReset(state?: string) {
    this.msalService.loginRedirect({
      authority: policies[RESET_PASSWORD].authority,
      scopes: ['openid', 'profile', 'email'],
      redirectUri: environment.sso.redirectUri,
      redirectStartPage: environment.sso.redirectUri,
      state: state || 'settings', // the component where call came from
    });
  }

  public redirectToLogin(state?: string) {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        prompt: 'login',
        redirectStartPage: environment.sso.redirectUri,
        // ...this.msalGuardConfig.authRequest,
        state: state || 'manual_login',
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect({
        prompt: 'login',
        redirectStartPage: environment.sso.redirectUri,
        state: state || 'manual_login',
      } as RedirectRequest);
    }
  }

  public logout() {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: environment.sso.redirectUri,
      state: 'logout',
    });
  }

  public showSsoPanel(user: User): void {
    if (!environment.sso.enabled) {
      return;
    }
    if (!user?.externalAuthId) {
      return;
    }

    let url = `https://portal.azure.com/${environment.sso.tenant}.onmicrosoft.com/#view/Microsoft_AAD_UsersAndTenants/UserProfileMenuBlade/~/overview/userId/${user.externalAuthId}/hidePreviewBanner~/true`

    this.httpService.openExternalUrl(url);
  }
}
